import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  LinearProgress,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Computer as ComputerIcon,
  Key as KeyIcon,
  Headphones as HeadphonesIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  CheckCircle as CheckInIcon,
  ExitToApp as CheckOutIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  snipeITApi,
  type SnipeITAsset,
  type SnipeITLicense,
  type SnipeITAccessory,
  type SnipeITConsumable,
  type SnipeITUser,
  type SnipeITStatusLabel,
  type SnipeITCheckoutRequest,
  type SnipeITCheckinRequest,
  type SnipeITLicenseSeat,
  type SnipeITLicenseSeatCheckoutRequest,
  type SnipeITAccessoryCheckoutRequest,
  type SnipeITConsumableCheckoutRequest,
} from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const AssetVerwaltung: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Data states
  const [assets, setAssets] = useState<SnipeITAsset[]>([]);
  const [licenses, setLicenses] = useState<SnipeITLicense[]>([]);
  const [accessories, setAccessories] = useState<SnipeITAccessory[]>([]);
  const [consumables, setConsumables] = useState<SnipeITConsumable[]>([]);
  const [users, setUsers] = useState<SnipeITUser[]>([]);
  const [statusLabels, setStatusLabels] = useState<SnipeITStatusLabel[]>([]);

  // Asset Checkout/Checkin Dialog
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<SnipeITAsset | null>(null);
  const [checkoutData, setCheckoutData] = useState<SnipeITCheckoutRequest>({
    checkoutToType: 'user',
    note: '',
  });
  const [checkinData, setCheckinData] = useState<SnipeITCheckinRequest>({
    note: '',
  });

  // License Seats Dialog
  const [licenseSeatsDialogOpen, setLicenseSeatsDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<SnipeITLicense | null>(null);
  const [licenseSeats, setLicenseSeats] = useState<SnipeITLicenseSeat[]>([]);
  const [licenseCheckoutDialogOpen, setLicenseCheckoutDialogOpen] = useState(false);
  const [selectedLicenseSeat, setSelectedLicenseSeat] = useState<SnipeITLicenseSeat | null>(null);
  const [licenseCheckoutData, setLicenseCheckoutData] = useState<SnipeITLicenseSeatCheckoutRequest>({});

  // Accessory Checkout Dialog
  const [accessoryCheckoutDialogOpen, setAccessoryCheckoutDialogOpen] = useState(false);
  const [selectedAccessory, setSelectedAccessory] = useState<SnipeITAccessory | null>(null);
  const [accessoryCheckoutData, setAccessoryCheckoutData] = useState<SnipeITAccessoryCheckoutRequest>({
    assignedTo: 0,
  });

  // Consumable Checkout Dialog
  const [consumableCheckoutDialogOpen, setConsumableCheckoutDialogOpen] = useState(false);
  const [selectedConsumable, setSelectedConsumable] = useState<SnipeITConsumable | null>(null);
  const [consumableCheckoutData, setConsumableCheckoutData] = useState<SnipeITConsumableCheckoutRequest>({
    assignedTo: 0,
  });

  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    checkConfiguration();
  }, []);

  useEffect(() => {
    if (configured) {
      loadData();
    }
  }, [configured, tabIndex]);

  const checkConfiguration = async () => {
    try {
      const response = await snipeITApi.getStatus();
      setConfigured(response.data.configured);
      if (!response.data.configured) {
        setError('Snipe-IT ist nicht konfiguriert. Bitte API-URL und API-Key in appsettings.json hinterlegen.');
        setLoading(false);
      }
    } catch (err: any) {
      setError('Fehler beim Prüfen der Snipe-IT Konfiguration');
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = searchText ? { search: searchText, limit: 100 } : { limit: 100 };

      // Load data based on current tab
      switch (tabIndex) {
        case 0: // Assets
          const [assetsRes, statusRes] = await Promise.all([
            snipeITApi.getAssets(params),
            snipeITApi.getStatusLabels(),
          ]);
          setAssets(assetsRes.data.rows || []);
          setStatusLabels(statusRes.data.rows || []);
          break;
        case 1: // Lizenzen
          const licensesRes = await snipeITApi.getLicenses(params);
          setLicenses(licensesRes.data.rows || []);
          break;
        case 2: // Zubehör
          const accessoriesRes = await snipeITApi.getAccessories(params);
          setAccessories(accessoriesRes.data.rows || []);
          break;
        case 3: // Verbrauchsmaterial
          const consumablesRes = await snipeITApi.getConsumables(params);
          setConsumables(consumablesRes.data.rows || []);
          break;
        case 4: // Benutzer
          const usersRes = await snipeITApi.getUsers(params);
          setUsers(usersRes.data.rows || []);
          break;
      }
    } catch (err: any) {
      console.error('Snipe-IT Fehler:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadUsersForCheckout = async () => {
    try {
      const usersRes = await snipeITApi.getUsers({ limit: 100 });
      setUsers(usersRes.data.rows || []);
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
    }
  };

  const handleSearch = () => {
    loadData();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Asset Check-out
  const openCheckoutDialog = (asset: SnipeITAsset) => {
    setSelectedAsset(asset);
    setCheckoutData({
      checkoutToType: 'user',
      note: '',
    });
    loadUsersForCheckout();
    setCheckoutDialogOpen(true);
  };

  const handleCheckout = async () => {
    if (!selectedAsset) return;
    try {
      setProcessingAction(true);
      await snipeITApi.checkoutAsset(selectedAsset.id, checkoutData);
      setCheckoutDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Checkout');
    } finally {
      setProcessingAction(false);
    }
  };

  // Asset Check-in
  const openCheckinDialog = (asset: SnipeITAsset) => {
    setSelectedAsset(asset);
    setCheckinData({ note: '' });
    setCheckinDialogOpen(true);
  };

  const handleCheckin = async () => {
    if (!selectedAsset) return;
    try {
      setProcessingAction(true);
      await snipeITApi.checkinAsset(selectedAsset.id, checkinData);
      setCheckinDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Checkin');
    } finally {
      setProcessingAction(false);
    }
  };

  // License Seats
  const openLicenseSeatsDialog = async (license: SnipeITLicense) => {
    setSelectedLicense(license);
    try {
      const seatsRes = await snipeITApi.getLicenseSeats(license.id);
      setLicenseSeats(seatsRes.data.rows || []);
      setLicenseSeatsDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Lizenzsitze');
    }
  };

  const openLicenseCheckoutDialog = (seat: SnipeITLicenseSeat) => {
    setSelectedLicenseSeat(seat);
    setLicenseCheckoutData({});
    loadUsersForCheckout();
    setLicenseCheckoutDialogOpen(true);
  };

  const handleLicenseCheckout = async () => {
    if (!selectedLicense || !selectedLicenseSeat) return;
    try {
      setProcessingAction(true);
      await snipeITApi.checkoutLicenseSeat(selectedLicense.id, selectedLicenseSeat.id, licenseCheckoutData);
      setLicenseCheckoutDialogOpen(false);
      // Reload seats
      const seatsRes = await snipeITApi.getLicenseSeats(selectedLicense.id);
      setLicenseSeats(seatsRes.data.rows || []);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Lizenz-Checkout');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleLicenseCheckin = async (seat: SnipeITLicenseSeat) => {
    if (!selectedLicense) return;
    try {
      setProcessingAction(true);
      await snipeITApi.checkinLicenseSeat(selectedLicense.id, seat.id);
      // Reload seats
      const seatsRes = await snipeITApi.getLicenseSeats(selectedLicense.id);
      setLicenseSeats(seatsRes.data.rows || []);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Lizenz-Checkin');
    } finally {
      setProcessingAction(false);
    }
  };

  // Accessory Checkout
  const openAccessoryCheckoutDialog = (accessory: SnipeITAccessory) => {
    setSelectedAccessory(accessory);
    setAccessoryCheckoutData({ assignedTo: 0 });
    loadUsersForCheckout();
    setAccessoryCheckoutDialogOpen(true);
  };

  const handleAccessoryCheckout = async () => {
    if (!selectedAccessory || !accessoryCheckoutData.assignedTo) return;
    try {
      setProcessingAction(true);
      await snipeITApi.checkoutAccessory(selectedAccessory.id, accessoryCheckoutData);
      setAccessoryCheckoutDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Zubehör-Checkout');
    } finally {
      setProcessingAction(false);
    }
  };

  // Consumable Checkout
  const openConsumableCheckoutDialog = (consumable: SnipeITConsumable) => {
    setSelectedConsumable(consumable);
    setConsumableCheckoutData({ assignedTo: 0 });
    loadUsersForCheckout();
    setConsumableCheckoutDialogOpen(true);
  };

  const handleConsumableCheckout = async () => {
    if (!selectedConsumable || !consumableCheckoutData.assignedTo) return;
    try {
      setProcessingAction(true);
      await snipeITApi.checkoutConsumable(selectedConsumable.id, consumableCheckoutData);
      setConsumableCheckoutDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Verbrauchsmaterial-Checkout');
    } finally {
      setProcessingAction(false);
    }
  };

  const getStatusColor = (statusType?: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (statusType?.toLowerCase()) {
      case 'deployable':
        return 'success';
      case 'deployed':
        return 'info';
      case 'pending':
        return 'warning';
      case 'archived':
      case 'undeployable':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!configured && !loading) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6">Snipe-IT nicht konfiguriert</Typography>
          <Typography>
            Bitte konfigurieren Sie die Snipe-IT Verbindung in der appsettings.json:
          </Typography>
          <Box component="pre" sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
{`"SnipeIT": {
  "BaseUrl": "http://your-snipeit-server",
  "ApiKey": "your-api-key"
}`}
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ComputerIcon color="primary" />
          <Typography variant="h5">Asset-Verwaltung (Snipe-IT)</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <TextField
            size="small"
            placeholder="Suchen..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab icon={<ComputerIcon />} label="Assets" iconPosition="start" />
          <Tab icon={<KeyIcon />} label="Lizenzen" iconPosition="start" />
          <Tab icon={<HeadphonesIcon />} label="Zubehör" iconPosition="start" />
          <Tab icon={<InventoryIcon />} label="Verbrauchsmaterial" iconPosition="start" />
          <Tab icon={<PersonIcon />} label="Benutzer" iconPosition="start" />
        </Tabs>

        {loading && <LinearProgress />}

        {/* Assets Tab */}
        <TabPanel value={tabIndex} index={0}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Modell</TableCell>
                  <TableCell>Seriennummer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Zugewiesen an</TableCell>
                  <TableCell>Standort</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Keine Assets gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((asset) => (
                    <TableRow key={asset.id} hover>
                      <TableCell>
                        <Chip label={asset.assetTag} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{asset.name}</TableCell>
                      <TableCell>{asset.model?.name || '-'}</TableCell>
                      <TableCell>{asset.serial || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={asset.statusLabel?.name || 'Unbekannt'}
                          size="small"
                          color={getStatusColor(asset.statusLabel?.statusType)}
                        />
                      </TableCell>
                      <TableCell>
                        {asset.assignedTo ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PersonIcon fontSize="small" />
                            {asset.assignedTo.name}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {asset.location ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationIcon fontSize="small" />
                            {asset.location.name}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {asset.assignedTo ? (
                          <Tooltip title="Check-in (Rückgabe)">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => openCheckinDialog(asset)}
                            >
                              <CheckInIcon />
                            </IconButton>
                          </Tooltip>
                        ) : asset.statusLabel?.statusType === 'deployable' ? (
                          <Tooltip title="Check-out (Ausgeben)">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openCheckoutDialog(asset)}
                            >
                              <CheckOutIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Lizenzen Tab */}
        <TabPanel value={tabIndex} index={1}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Hersteller</TableCell>
                  <TableCell>Produkt-Key</TableCell>
                  <TableCell>Sitze (Frei/Total)</TableCell>
                  <TableCell>Ablaufdatum</TableCell>
                  <TableCell>Kaufdatum</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {licenses.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Keine Lizenzen gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  licenses.map((license) => (
                    <TableRow key={license.id} hover>
                      <TableCell>{license.name}</TableCell>
                      <TableCell>{license.manufacturer?.name || '-'}</TableCell>
                      <TableCell>
                        {license.productKey ? (
                          <code>{license.productKey.substring(0, 10)}...</code>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${license.freeSeatsCount}/${license.seats}`}
                          size="small"
                          color={license.freeSeatsCount === 0 ? 'error' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        {license.expirationDate?.formatted || '-'}
                      </TableCell>
                      <TableCell>
                        {license.purchaseDate?.formatted || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Sitze verwalten">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openLicenseSeatsDialog(license)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Zubehör Tab */}
        <TabPanel value={tabIndex} index={2}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Kategorie</TableCell>
                  <TableCell>Hersteller</TableCell>
                  <TableCell>Modell-Nr.</TableCell>
                  <TableCell>Verfügbar/Total</TableCell>
                  <TableCell>Standort</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accessories.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Kein Zubehör gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  accessories.map((accessory) => (
                    <TableRow key={accessory.id} hover>
                      <TableCell>{accessory.name}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CategoryIcon fontSize="small" />
                          {accessory.category?.name || '-'}
                        </Box>
                      </TableCell>
                      <TableCell>{accessory.manufacturer?.name || '-'}</TableCell>
                      <TableCell>{accessory.modelNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${accessory.remainingQty}/${accessory.qty}`}
                          size="small"
                          color={accessory.remainingQty === 0 ? 'error' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        {accessory.location ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationIcon fontSize="small" />
                            {accessory.location.name}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {accessory.remainingQty > 0 && (
                          <Tooltip title="Ausgeben">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openAccessoryCheckoutDialog(accessory)}
                            >
                              <CheckOutIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Verbrauchsmaterial Tab */}
        <TabPanel value={tabIndex} index={3}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Kategorie</TableCell>
                  <TableCell>Artikel-Nr.</TableCell>
                  <TableCell>Verbleibend/Total</TableCell>
                  <TableCell>Standort</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consumables.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Kein Verbrauchsmaterial gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  consumables.map((consumable) => (
                    <TableRow key={consumable.id} hover>
                      <TableCell>{consumable.name}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CategoryIcon fontSize="small" />
                          {consumable.category?.name || '-'}
                        </Box>
                      </TableCell>
                      <TableCell>{consumable.itemNo || consumable.modelNumber || '-'}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={`${consumable.remaining}/${consumable.qty}`}
                            size="small"
                            color={consumable.remaining < 5 ? 'error' : consumable.remaining < 10 ? 'warning' : 'success'}
                          />
                          {consumable.remaining < 5 && (
                            <Tooltip title="Niedriger Bestand!">
                              <WarningIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {consumable.location ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationIcon fontSize="small" />
                            {consumable.location.name}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {consumable.remaining > 0 && (
                          <Tooltip title="Ausgeben">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openConsumableCheckoutDialog(consumable)}
                            >
                              <CheckOutIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Benutzer Tab */}
        <TabPanel value={tabIndex} index={4}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Benutzername</TableCell>
                  <TableCell>E-Mail</TableCell>
                  <TableCell>Abteilung</TableCell>
                  <TableCell>Standort</TableCell>
                  <TableCell>Assets</TableCell>
                  <TableCell>Lizenzen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Keine Benutzer gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" />
                          {user.name}
                        </Box>
                      </TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>{user.department?.name || '-'}</TableCell>
                      <TableCell>
                        {user.location ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationIcon fontSize="small" />
                            {user.location.name}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={user.assetsCount} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={user.licensesCount} size="small" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Asset Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onClose={() => setCheckoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Asset ausgeben (Check-out)
        </DialogTitle>
        <DialogContent>
          {selectedAsset && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  {selectedAsset.name} ({selectedAsset.assetTag})
                </Typography>
                {selectedAsset.model?.name && (
                  <Typography variant="body2">Modell: {selectedAsset.model.name}</Typography>
                )}
              </Alert>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Ausgeben an</InputLabel>
                <Select
                  value={checkoutData.checkoutToType}
                  label="Ausgeben an"
                  onChange={(e) =>
                    setCheckoutData({ ...checkoutData, checkoutToType: e.target.value as 'user' | 'asset' | 'location' })
                  }
                >
                  <MenuItem value="user">Benutzer</MenuItem>
                  <MenuItem value="location">Standort</MenuItem>
                </Select>
              </FormControl>

              {checkoutData.checkoutToType === 'user' && (
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.name} (${option.username})`}
                  onChange={(_, value) =>
                    setCheckoutData({ ...checkoutData, assignedUser: value?.id })
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Benutzer auswählen" fullWidth sx={{ mb: 2 }} />
                  )}
                />
              )}

              <TextField
                label="Notiz (optional)"
                value={checkoutData.note || ''}
                onChange={(e) => setCheckoutData({ ...checkoutData, note: e.target.value })}
                multiline
                rows={2}
                fullWidth
                sx={{ mb: 2 }}
              />

              <TextField
                label="Erwartete Rückgabe (optional)"
                type="date"
                value={checkoutData.expectedCheckin || ''}
                onChange={(e) => setCheckoutData({ ...checkoutData, expectedCheckin: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckoutDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCheckout}
            disabled={processingAction || (checkoutData.checkoutToType === 'user' && !checkoutData.assignedUser)}
            startIcon={processingAction ? <CircularProgress size={20} /> : <CheckOutIcon />}
          >
            Ausgeben
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asset Checkin Dialog */}
      <Dialog open={checkinDialogOpen} onClose={() => setCheckinDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Asset zurücknehmen (Check-in)
        </DialogTitle>
        <DialogContent>
          {selectedAsset && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  {selectedAsset.name} ({selectedAsset.assetTag})
                </Typography>
                {selectedAsset.assignedTo && (
                  <Typography variant="body2">
                    Aktuell bei: {selectedAsset.assignedTo.name}
                  </Typography>
                )}
              </Alert>

              <TextField
                label="Notiz (optional)"
                value={checkinData.note || ''}
                onChange={(e) => setCheckinData({ ...checkinData, note: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckinDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCheckin}
            disabled={processingAction}
            startIcon={processingAction ? <CircularProgress size={20} /> : <CheckInIcon />}
          >
            Zurücknehmen
          </Button>
        </DialogActions>
      </Dialog>

      {/* License Seats Dialog */}
      <Dialog open={licenseSeatsDialogOpen} onClose={() => setLicenseSeatsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Lizenzsitze verwalten - {selectedLicense?.name}
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sitz</TableCell>
                  <TableCell>Benutzer</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {licenseSeats.map((seat, index) => (
                  <TableRow key={seat.id} hover>
                    <TableCell>Sitz {index + 1}</TableCell>
                    <TableCell>
                      {seat.assignedUser ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PersonIcon fontSize="small" />
                          {seat.assignedUser.name}
                        </Box>
                      ) : (
                        <Chip label="Frei" size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      {seat.assignedAsset ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <ComputerIcon fontSize="small" />
                          {seat.assignedAsset.name}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {seat.assignedUser || seat.assignedAsset ? (
                        <Tooltip title="Freigeben">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleLicenseCheckin(seat)}
                            disabled={processingAction}
                          >
                            <CheckInIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Zuweisen">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openLicenseCheckoutDialog(seat)}
                          >
                            <CheckOutIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLicenseSeatsDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* License Seat Checkout Dialog */}
      <Dialog open={licenseCheckoutDialogOpen} onClose={() => setLicenseCheckoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Lizenzsitz zuweisen
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => `${option.name} (${option.username})`}
              onChange={(_, value) =>
                setLicenseCheckoutData({ ...licenseCheckoutData, assignedTo: value?.id })
              }
              renderInput={(params) => (
                <TextField {...params} label="Benutzer auswählen" fullWidth sx={{ mb: 2 }} />
              )}
            />

            <TextField
              label="Notiz (optional)"
              value={licenseCheckoutData.note || ''}
              onChange={(e) => setLicenseCheckoutData({ ...licenseCheckoutData, note: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLicenseCheckoutDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleLicenseCheckout}
            disabled={processingAction || !licenseCheckoutData.assignedTo}
            startIcon={processingAction ? <CircularProgress size={20} /> : <CheckOutIcon />}
          >
            Zuweisen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accessory Checkout Dialog */}
      <Dialog open={accessoryCheckoutDialogOpen} onClose={() => setAccessoryCheckoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Zubehör ausgeben
        </DialogTitle>
        <DialogContent>
          {selectedAccessory && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">{selectedAccessory.name}</Typography>
                <Typography variant="body2">
                  Verfügbar: {selectedAccessory.remainingQty} von {selectedAccessory.qty}
                </Typography>
              </Alert>

              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.name} (${option.username})`}
                onChange={(_, value) =>
                  setAccessoryCheckoutData({ ...accessoryCheckoutData, assignedTo: value?.id || 0 })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Benutzer auswählen" fullWidth sx={{ mb: 2 }} />
                )}
              />

              <TextField
                label="Notiz (optional)"
                value={accessoryCheckoutData.note || ''}
                onChange={(e) => setAccessoryCheckoutData({ ...accessoryCheckoutData, note: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessoryCheckoutDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAccessoryCheckout}
            disabled={processingAction || !accessoryCheckoutData.assignedTo}
            startIcon={processingAction ? <CircularProgress size={20} /> : <CheckOutIcon />}
          >
            Ausgeben
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consumable Checkout Dialog */}
      <Dialog open={consumableCheckoutDialogOpen} onClose={() => setConsumableCheckoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Verbrauchsmaterial ausgeben
        </DialogTitle>
        <DialogContent>
          {selectedConsumable && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">{selectedConsumable.name}</Typography>
                <Typography variant="body2">
                  Verbleibend: {selectedConsumable.remaining} von {selectedConsumable.qty}
                </Typography>
              </Alert>

              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.name} (${option.username})`}
                onChange={(_, value) =>
                  setConsumableCheckoutData({ ...consumableCheckoutData, assignedTo: value?.id || 0 })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Benutzer auswählen" fullWidth sx={{ mb: 2 }} />
                )}
              />

              <TextField
                label="Notiz (optional)"
                value={consumableCheckoutData.note || ''}
                onChange={(e) => setConsumableCheckoutData({ ...consumableCheckoutData, note: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsumableCheckoutDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConsumableCheckout}
            disabled={processingAction || !consumableCheckoutData.assignedTo}
            startIcon={processingAction ? <CircularProgress size={20} /> : <CheckOutIcon />}
          >
            Ausgeben
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetVerwaltung;
